"use client";

import { useSearchParams } from "next/navigation";
import { useCallback } from "react";

/**
 * Landing-page invite links carry ?promotionalCode & ?builderPartnerEmail
 * (the partner-specific name avoids colliding with the welcome-code flow's
 * ?email on marketing pages). Checkout expects the buyer email as `email`,
 * so any CTA that points at /checkout forwards both, translating the email
 * param. Non-checkout hrefs pass through untouched.
 *
 * Values are never pre-encoded — URL.searchParams.set percent-encodes.
 */
// A relative href needs some base to parse. The base never reaches the
// output (relative in → relative out; absolute in → base ignored), so this
// only matters during SSR where window is unavailable — the browser always
// passes the real origin.
const SSR_PARSE_BASE = "https://yardzen.com";

export function appendBuilderPartnerParams(
  href: string,
  params: {
    promotionalCode?: string | null;
    builderPartnerEmail?: string | null;
  },
  baseUrl?: string,
): string {
  const { promotionalCode, builderPartnerEmail } = params;
  if (!promotionalCode && !builderPartnerEmail) return href;

  let url: URL;
  try {
    url = new URL(href, baseUrl || SSR_PARSE_BASE);
  } catch {
    return href;
  }
  if (!url.pathname.startsWith("/checkout")) return href;

  // The visitor's link param wins over a promo baked into the CTA href
  // (e.g. a site-wide banner code in the Contentful CTA url) — an invite's
  // buyer-bound code must reach checkout intact.
  if (promotionalCode) {
    url.searchParams.set("promotionalCode", promotionalCode);
  }
  if (builderPartnerEmail && !url.searchParams.has("email")) {
    url.searchParams.set("email", builderPartnerEmail);
  }
  return href.startsWith("http")
    ? url.toString()
    : url.pathname + url.search + url.hash;
}

/** Returns a wrapper that forwards the current page's partner link params
 *  onto a checkout href. Identity for non-checkout or empty hrefs. */
export function useBuilderPartnerCheckoutHref() {
  const searchParams = useSearchParams();
  return useCallback(
    <T extends string | undefined>(href: T): T => {
      if (!href || !searchParams) return href;
      return appendBuilderPartnerParams(
        href,
        {
          promotionalCode: searchParams.get("promotionalCode"),
          builderPartnerEmail: searchParams.get("builderPartnerEmail"),
        },
        typeof window === "undefined" ? undefined : window.location.origin,
      ) as T;
    },
    [searchParams],
  );
}
